import { Proposition, PropositionProductType } from "@dune/types";
import { Badge } from "@dune/ui";
import {
	formatCurrency,
	formatSimpleDate,
	getPropositionStatusBadgeClasses,
	getPropositionStatusLabel,
	getActionTypesLabelNames,
	getBuildingTypeLabelName,
} from "@dune/utils";
import { ColumnDef } from "@tanstack/react-table";

export const propositionsColumns = (): ColumnDef<Proposition>[] => [
	{
		accessorKey: "reference",
		header: "Ref",
		cell: ({ row }) => (
			<div className="font-medium">{row.getValue("reference")}</div>
		),
	},
	{
		accessorKey: "brokerSociety",
		header: "Apporteur(s)",
		cell: ({ row }) => {
			const broker = row.original.brokerSociety;
			return (
				<div>
					<div className="font-medium">{broker?.denomination || "N/A"}</div>
				</div>
			);
		},
	},
	{
		accessorKey: "constructionSite",
		header: "Maître d'ouvrage",
		cell: ({ row }) => {
			const site =
				row.original.constructionSite || row.original.gfaConstructionSite;
			const clientDetails = site?.constructionSiteClientDetails;
			if (!clientDetails) return <div>N/A</div>;

			let moaName: string | undefined;
			if (clientDetails.moaClientType === "SOCIETY") {
				moaName = clientDetails.moaClientSociety?.denomination;
			} else if (clientDetails.moaClientType === "USER") {
				const first = clientDetails.moaClientContact?.firstname || "";
				const last = clientDetails.moaClientContact?.lastname || "";
				moaName = `${first} ${last}`.trim();
			}

			return (
				<div>
					<div className="font-medium">{moaName || "Pas de MOA identifié"}</div>
				</div>
			);
		},
	},
	{
		accessorKey: "constructionType",
		header: "Type de projet",
		cell: ({ row }) => {
			const site =
				row.original.constructionSite || row.original.gfaConstructionSite;
			const actions = site?.constructionSiteDetails?.constructionSiteAction;
			const primaryAction = actions;
			const actionTypes = primaryAction?.types || [];
			const translatedTypes = getActionTypesLabelNames(actionTypes);
			return (
				<div>
					<div className="font-medium">{translatedTypes || "N/A"}</div>
				</div>
			);
		},
	},
	{
		accessorKey: "constructionSiteType",
		header: "Type de chantier",
		cell: ({ row }) => {
			const site =
				row.original.constructionSite || row.original.gfaConstructionSite;
			const actions = site?.constructionSiteDetails?.constructionSiteAction;
			const primaryAction = actions;
			const buildingType = primaryAction?.buildingType;
			const translatedType = getBuildingTypeLabelName(buildingType || "");
			return (
				<div>
					<div className="font-medium">{translatedType || "N/A"}</div>
				</div>
			);
		},
	},
	{
		accessorKey: "totalCost",
		header: "Coût",
		cell: ({ row }) => {
			const site =
				row.original.constructionSite || row.original.gfaConstructionSite;
			const details = site?.constructionSiteDetails;
			const amountTTC = details?.totalAmountTTC;
			const amountHT = details?.totalAmountHT;
			if (!amountTTC && !amountHT)
				return <span className="">Non renseigné</span>;

			return (
				<div className="font-medium">
					{amountHT
						? `${formatCurrency(amountHT)} HT`
						: amountTTC
							? `${formatCurrency(amountTTC)} TTC`
							: "N/A"}
				</div>
			);
		},
	},
	{
		accessorKey: "address",
		header: "Adresse",
		cell: ({ row }) => {
			const site =
				row.original.constructionSite || row.original.gfaConstructionSite;
			const address = site?.constructionSiteDetails?.address;
			return (
				<div>
					<div className="font-medium">
						{address?.streetNumber || ""} {address?.streetName || "N/A"}
					</div>
					<div className="text-sm text-gray-500">
						{address?.city || ""} {address?.postalCode || ""}
					</div>
				</div>
			);
		},
	},
	{
		accessorKey: "status",
		header: "Statut",
		cell: ({ row }) => {
			const status = row.original.status;
			const classes = getPropositionStatusBadgeClasses(status);
			const label = getPropositionStatusLabel(status);
			return (
				<div
					className={`inline-flex items-center px-2 py-1 rounded-full text-xs border whitespace-nowrap ${classes}`}
				>
					{label}
				</div>
			);
		},
	},
	{
		accessorKey: "createdAt",
		header: "Créé",
		cell: ({ row }) => (
			<div className="text-sm">
				{formatSimpleDate(row.getValue("createdAt"))}
			</div>
		),
	},
	{
		accessorKey: "productType",
		header: "Produit",
		cell: ({ row }) => {
			const type = row.original.productType;
			const typeLabels = {
				[PropositionProductType.DO]: "DO",
				[PropositionProductType.TRC]: "TRC",
				[PropositionProductType.MRT]: "MRT",
				[PropositionProductType.GFA]: "GFA",
			};
			return (
				<Badge variant="outline">
					{typeLabels[type as keyof typeof typeLabels] || type}
				</Badge>
			);
		},
	},
	{
		accessorKey: "primeTTC",
		header: "Prime TTC",
		cell: ({ row }) => {
			const site =
				row.original.constructionSite || row.original.gfaConstructionSite;
			const offerOfProductType = site?.constructionSiteOffers.find(
				(offer) =>
					offer.binderWarranty.warrantyType === row.original.productType,
			);
			const totalClientPrimeTTC = offerOfProductType?.pricingDetails.find(
				(pricing) => pricing.type === "INITIAL",
			)?.totalClientPrimeTTC;
			return (
				<div className="font-medium">
					{formatCurrency(totalClientPrimeTTC ?? 0)}
				</div>
			);
		},
	},
	// actions column removed
];
